import React, {useEffect, useMemo, useRef} from 'react';
import HybridDeploymentMgr from 'hybridDeployment/HybridDeploymentMgr';
import {RouteInfo} from 'sfccAPI/interfaces';

import LoaderComponent from 'coreComponents/LoaderComponent';
import {useClientServerEffect, useLocalesMgr, useLocation, useRequestClass, useSSRRedirect, useSSRRequest} from 'coreHooks';
import useServerData from 'coreHooks/useServerData';
import {RouterLocation} from 'coreInterfaces/common/interfaces';
import {suspenseComponent} from 'coreUtils/adapters/lazy';
import {Redirect} from 'coreUtils/adapters/router';
import {parsePreviewEntryPath} from 'coreUtils/cms/previewEntry';
import {getEncodedURI, isSfccURL, setTabURL} from 'coreUtils/url';
import {isServer} from 'coreUtils/utils';

import {useGeneralProvider} from 'brand/app/components/_app-config/GeneralProvider';

import CategoryPage from './subComponents/CategoryPage';
import ContentPage from './subComponents/ContentPage';
import RouteManager from './RouteManager';

// CMS preview only: its own chunk, out of the bundle of the storefront
const PreviewEntryPage = suspenseComponent<{entryId: string}>(/* #__LOADABLE__ */ () => import('coreComponents/PreviewEntryPage'));

const NOT_FOUND_ACTION = 'Home-ErrorNotFound';
const BANNED_PARAMETERS = ['page'];
const CONTINUE_SHOPPING_ACTIONS = ['^Product-Show$', '^Search-Show$'];

const Effects = ({routeInfo}: {routeInfo: RouteInfo}) => {
	const location: RouterLocation = useLocation();
	const localesMgr = useLocalesMgr();

	const updateURLWithRouteDetails = () => {
		if (routeInfo?.url && routeInfo.action !== NOT_FOUND_ACTION) {
			const routeDetailsURL = new URL(routeInfo?.url, window.location.origin);
			const locationParams = new URLSearchParams(location.search);

			for (let [key, val] of Object.entries(routeDetailsURL.searchParams)) {
				locationParams.append(key, val);
			}

			const updatedURL = localesMgr.updateLinkWithLocale(
				`${routeDetailsURL.pathname}?${locationParams.toString()}`,
			);

			setTabURL(updatedURL);
		}
	};

	useEffect(updateURLWithRouteDetails, []);

	useEffect(updateURLWithRouteDetails, [routeInfo]);

	useEffect(() => {
		if (
			routeInfo?.action &&
			CONTINUE_SHOPPING_ACTIONS.some((pattern) => routeInfo.action.match(new RegExp(pattern))) &&
			!location.pathname.includes('COCart-Show')
		) {
			HybridDeploymentMgr.saveCurrentRouteToHistory();
		}
	}, [routeInfo, location.pathname]);

	return null;
};

const SfccRoute = () => {
	const location: RouterLocation = useLocation();
	const {managerRepository} = useGeneralProvider();
	const redirect = useSSRRedirect();
	const localesMgr = useLocalesMgr();
	const currentLanguageCode = localesMgr.getCurrentLanguageCode(true);
	const redirectRef = useRef(null);
	const {query, routeInfoQuery, additionalQuery, normalizedPath, locationPathnameWithLocale, url} = useMemo(() => {
		let normalizedPath: string;
		const url = location.search ? `${location.pathname}${location.search}` : location.pathname;
		const locationPathnameWithLocale = localesMgr.updateLinkWithLocale(url);
		const query = Object.fromEntries(new URLSearchParams(location.search).entries());
		const routeInfoQuery = {...query};
		const additionalQuery = {
			country: localesMgr.getDefaultLocale().getCountryCode(),
		};

		try {
			normalizedPath = localesMgr.normalizeLink(location.pathname);
		} catch (e) {
			console.error(e);
		}

		if (localesMgr.getCurrentLocale().isValid()) {
			additionalQuery.country = localesMgr.getCurrentCountry().getCountryCode();
		}

		if (Object.keys(routeInfoQuery).length > 0) {
			BANNED_PARAMETERS.forEach((p) => {
				delete routeInfoQuery[p];
			});
		}

		return {
			url,
			query,
			normalizedPath,
			routeInfoQuery,
			additionalQuery,
			locationPathnameWithLocale,
		};
	}, [location.pathname, location.search]);

	const routeInfo = useServerData<RouteInfo>(
		'api-route-info',
		async () => {
			const routeInfoCallResult = await managerRepository.getSfccAPI().getRouteInfo(getEncodedURI(normalizedPath), {
				...routeInfoQuery,
				...additionalQuery,
			});

			if (routeInfoCallResult?.alternativeUrl) {
				const alternativeUrlWithLocale = localesMgr.updateLinkWithLocale(routeInfoCallResult.alternativeUrl);

				if (alternativeUrlWithLocale !== locationPathnameWithLocale) {
					const altLocation = new URL(routeInfoCallResult.alternativeUrl, 'http://localhost');

					return await managerRepository
						.getSfccAPI()
						.getRouteInfo(getEncodedURI(localesMgr.normalizeLink(altLocation.pathname)), {
							...Object.fromEntries(new URLSearchParams(altLocation.search).entries()),
							...additionalQuery,
						});
				}
			}

			return routeInfoCallResult;
		},
		[normalizedPath],
	);

	useClientServerEffect(() => {
		if (routeInfo.redirectsStack && routeInfo.redirectsStack.length > 0) {
			const redirectStack = routeInfo.redirectsStack[0];

			if (!isSfccURL(redirectStack.location.href)) {
				redirectRef.current = {
					status: redirectStack.status,
					url: redirectStack.location.href
				};
			} else {
				const redirectURL = localesMgr.updateLinkWithLocale(redirectStack.location.href);

				if (url !== redirectURL) {
					redirectRef.current = {
						status: redirectStack.status,
						url: redirectURL
					};
				}
			}
		}
	}, [routeInfo, url]);

	const {mergedQuery, isHomePage, slug} = useMemo(() => {
		const isHomePage =
			['Home-Show', 'Default-Start'].indexOf(routeInfo.action) !== -1 ||
			'/' + currentLanguageCode + '-' + additionalQuery?.country?.toLowerCase() + '/' === location.pathname;
		const slug = isHomePage ? '/' : localesMgr.getUrlWithoutLocale(location.pathname);
		const normalizedRouteQueryParams: Record<string, unknown> = Object.assign({}, routeInfo?.queryParams || {});

		if (typeof normalizedRouteQueryParams.q === 'string') {
			const qValue = normalizedRouteQueryParams.q;

			try {
				normalizedRouteQueryParams.q = decodeURIComponent(qValue.replace(/\+/g, '%20'));
			} catch (e) {
				normalizedRouteQueryParams.q = qValue.replace(/\+/g, ' ');
			}
		}

		const mergedQuery = Object.assign({}, normalizedRouteQueryParams, query);

		return {
			slug,
			isHomePage,
			mergedQuery,
		};
	}, [routeInfo, location.pathname]);

	const ComponentToLoad = useMemo(() => {
		const currentRoute = RouteManager.getRoute(routeInfo.action);

		return currentRoute?.component || LoaderComponent;
	}, [routeInfo]);

	if (redirectRef.current) {
		const url = redirectRef.current.url;
		const status = redirectRef.current.status;

		redirectRef.current = null;

		redirect(status, url);

		return <Redirect to={url} />;
	} else if (!normalizedPath) {
		redirect(301, '/');

		return <Redirect to={'/'} />;
	} else if ([NOT_FOUND_ACTION].indexOf(routeInfo.action) !== -1 || isHomePage) {
		return (
			<>
				<Effects routeInfo={routeInfo} />
				<ContentPage
					key={slug}
					ComponentToLoad={ComponentToLoad}
					routeInfo={routeInfo}
					slug={slug}
					isHomePage={isHomePage}
				/>
			</>
		);
	} else if (routeInfo.action === 'Search-Show') {
		return (
			<>
				<CategoryPage mergedQuery={mergedQuery} routeInfo={routeInfo} ComponentToLoad={ComponentToLoad} />
			</>
		);
	} else {
		return <ComponentToLoad routeInfo={routeInfo} />;
	}
};

/**
 * Page of the current URL: the SFCC route (routeInfo), or in CMS preview the isolated render of an entry
 * (`coreUtils/cms/previewEntry`), which has no route.
 */
const APIRouter = () => {
	const location: RouterLocation = useLocation();
	const localesMgr = useLocalesMgr();
	const {managerRepository} = useGeneralProvider();
	const req = useSSRRequest();
	const {isCmsPreview, isLivePreview} = useRequestClass();
	const previewEntryId = useMemo(() => {
		try {
			return parsePreviewEntryPath(localesMgr.getUrlWithoutLocale(location.pathname));
		} catch {
			return null;
		}
	}, [location.pathname]);

	if (isServer && (req?.data?.scheduledAt || req?.cmsCpaConnection)) {
		// TODO: MOVE
		managerRepository.getContentMgr().setSSRPreviewParams(req.data?.scheduledAt, req.cmsCpaConnection);
	}

	if (previewEntryId && (isCmsPreview || isLivePreview)) {
		return <PreviewEntryPage entryId={previewEntryId} />;
	}

	return <SfccRoute />;
};

export default APIRouter;
